/**
 * saveTheme — the DEDICATED, versionless theme save channel (feature 0019 Slice 2, BLOCKER #1).
 *
 * The page-draft autosave engine (`autosave.ts`) is single-target: it serializes `{...meta, sections}`
 * (no theme slot), PUTs `/api/builder/page` under an `@Version` optimistic lock, and reconciles 409s.
 * Routing a theme edit through that engine would fire a page PUT that silently drops the theme. So theme
 * gets its OWN engine here — mirror of autosave's debounce + single-flight + offline-backoff + tab-hide
 * keepalive, MINUS versioning / publish / 409-reconcile:
 *
 *  - Theme is a single-writer live row (`store_settings.data.themeSettings`), last-write-wins → NO
 *    `@Version`, NO conflict path. Every flush sends the LATEST `themeSettings` from the store.
 *  - A theme edit NEVER dirties or bumps the page `@Version`; it is excluded from the page
 *    `serialize()` / `keepaliveFlush()` / `publish()`. This module is the ONLY caller of
 *    `builderApi.saveTheme`.
 *  - Live-on-save (Q1): the write hits the live storefront row immediately — no draft/publish state.
 *
 * Kept framework-free (plain functions over the store) so it is unit-testable with fake timers and a
 * mocked api layer, and immune to StrictMode's double-effect. Mirrors `autosave.ts` on purpose.
 */
import { getToken } from '../boot/session';
import { isReadOnlyView } from '../boot/view-handoff';
import { deriveApiBaseUrl } from '../boot/config';
import { builderApi } from '../lib/builderApi';
import { isNetworkError } from '../lib/errors';
import { stripLocalThemeMedia } from '../lib/localMedia';
import { useBuilderStore } from './builderStore';

export const THEME_DEBOUNCE_MS = 800;
export const THEME_MAX_WAIT_MS = 5000;
const BACKOFF_MIN_MS = 2000;
const BACKOFF_MAX_MS = 30_000;

const THEME_PATH = '/api/store/settings/theme';

// Read the store through a getter, NEVER a module-eval `const` — builderStore.ts ↔ saveTheme.ts is a
// circular import (the store's `updateThemeSettings` calls `scheduleThemeSave`); a top-level capture
// would bind to `undefined`. A function reads the resolved live binding at CALL time. (mirror autosave.ts)
function store() {
  return useBuilderStore;
}

// ── debounce-with-max-wait timer state (module-scoped singletons) ─────────────
let debounceTimer: ReturnType<typeof setTimeout> | null = null;
let maxWaitTimer: ReturnType<typeof setTimeout> | null = null;
let backoffTimer: ReturnType<typeof setTimeout> | null = null;
let backoffMs = BACKOFF_MIN_MS;

/** A second edit arrived while a PUT was in flight → send exactly one more when it lands. */
let pendingFollowUp = false;
/**
 * SINGLE-FLIGHT guard — the source of truth for "a theme PUT is running", NOT `themeSaveState` (which an
 * edit could race). A dedicated flag can't be clobbered by a store update mid-flight. (mirror autosave.ts)
 */
let inFlight = false;
/** There is an unpersisted theme edit — drives the keepalive/backoff decision + the "no-op when clean". */
let pendingSave = false;

function clearDebounceTimers(): void {
  if (debounceTimer) {
    clearTimeout(debounceTimer);
    debounceTimer = null;
  }
  if (maxWaitTimer) {
    clearTimeout(maxWaitTimer);
    maxWaitTimer = null;
  }
}

function clearBackoff(): void {
  if (backoffTimer) {
    clearTimeout(backoffTimer);
    backoffTimer = null;
  }
}

function fire(): void {
  clearDebounceTimers();
  void flushThemeSave();
}

/**
 * Schedule a debounced theme save. Called from the store's `updateThemeSettings` (and by undo/redo when
 * the restored theme differs). Debounce resets per edit; the max-wait timer guarantees a flush during a
 * continuous burst (a slider/color drag) so the seller never drags for 5s with nothing saved.
 */
export function scheduleThemeSave(): void {
  if (isReadOnlyView()) return; // superadmin consultation: nothing may be written
  pendingSave = true;
  // STAGED-FOR-PUBLISH suppression (spec 0032 apply-theme): while a freshly-applied theme awaits the
  // seller's Publish, NO theme PUT may fire — the live storefront reads this row directly (0019 Q1
  // live-on-save), so an early write would re-skin the LIVE store before publish and break the
  // product promise ("your live store is unchanged until you publish"). Edits made meanwhile stay
  // local (pendingSave=true) and all flush together when publish() releases the staging.
  if (store().getState().themeStagedForPublish) return;
  if (debounceTimer) clearTimeout(debounceTimer);
  debounceTimer = setTimeout(fire, THEME_DEBOUNCE_MS);
  if (!maxWaitTimer) maxWaitTimer = setTimeout(fire, THEME_MAX_WAIT_MS);
}

/** Cancel any pending theme save (store teardown / test reset). */
export function cancelThemeSave(): void {
  clearDebounceTimers();
  clearBackoff();
  pendingFollowUp = false;
  inFlight = false;
  pendingSave = false;
}

/**
 * The core theme save. Single-flight, last-write-wins (no version). Resolves when this attempt settles;
 * never throws (errors reflect into `themeSaveState`). A mid-flight edit is detected by a serialized
 * marker (the theme changed while the PUT was in flight → one more flush is scheduled), so no edit is
 * silently dropped. (mirror autosave.ts `flushSave`)
 */
export async function flushThemeSave(): Promise<void> {
  clearDebounceTimers();
  if (isReadOnlyView()) return; // superadmin consultation: nothing may be written
  if (!pendingSave) return; // nothing unsaved
  // Staged themes never flush from here (keepalive/tab-hide included) — publish() releases them.
  if (store().getState().themeStagedForPublish) return;

  if (inFlight) {
    pendingFollowUp = true;
    return;
  }

  inFlight = true;
  store().setState({ themeSaveState: 'saving' });
  const theme = store().getState().themeSettings;
  const marker = JSON.stringify(theme);

  try {
    await builderApi.saveTheme(theme);
    backoffMs = BACKOFF_MIN_MS; // a success resets the offline backoff ladder
    clearBackoff();
    inFlight = false;
    // Still unsaved if the seller edited WHILE this PUT was in flight (marker changed) or a follow-up
    // was queued → schedule exactly one more flush for the newer theme.
    const changed = JSON.stringify(store().getState().themeSettings) !== marker;
    if (changed || pendingFollowUp) {
      pendingFollowUp = false;
      scheduleThemeSave();
    } else {
      pendingSave = false;
      // A successful PUT makes `theme` the authoritative LIVE row — keep the snapshot in lockstep
      // (it is what leaving a theme workspace restores).
      store().setState({
        themeSaveState: 'saved',
        themeSavedAt: Date.now(),
        liveThemeSettings: theme,
      });
    }
  } catch (e) {
    inFlight = false;
    handleThemeSaveError(e);
  }
}

function handleThemeSaveError(e: unknown): void {
  if (isNetworkError(e)) {
    // Offline / transport failure → keep pendingSave, back off, resume on reconnect. Don't lose the edit.
    store().setState({ themeSaveState: 'offline' });
    queueBackoffRetry();
    return;
  }
  // NoStoreError / SessionExpiredError / 422 (front already validates) / 5xx → generic error, keep
  // pendingSave so the next edit or a manual retry can recover. Theme is never version-conflicted.
  store().setState({ themeSaveState: 'error' });
}

/** Exponential backoff retry while offline; the `online` trigger also resumes immediately. */
function queueBackoffRetry(): void {
  clearBackoff();
  backoffTimer = setTimeout(() => {
    backoffTimer = null;
    if (pendingSave && store().getState().themeSaveState === 'offline') {
      void flushThemeSave();
    }
    backoffMs = Math.min(backoffMs * 2, BACKOFF_MAX_MS);
  }, backoffMs);
}

// ── keepalive last-save (tab-hide data-loss guard) ─────────────────────────────

/**
 * Persist the latest theme on a hard tab-close while a debounced edit is still pending. Uses
 * `fetch(..., { keepalive: true })` — which, unlike `navigator.sendBeacon`, carries the
 * `Authorization: Bearer` header, so the PUT authenticates and survives the page going away. No version,
 * so a keepalive theme PUT is always accepted (last-write-wins). Returns true if a request was dispatched.
 */
export function keepaliveThemeFlush(): boolean {
  clearDebounceTimers();
  if (isReadOnlyView()) return false; // superadmin consultation: nothing may be written
  // STAGED-FOR-PUBLISH suppression (spec 0032) — MUST mirror scheduleThemeSave (line 91) and
  // flushThemeSave (line 116): a freshly-applied theme awaiting Publish stages presetId + colors
  // LOCALLY, and `scheduleThemeSave` arms `pendingSave` the moment the workspace is entered (before
  // its own staged early-return). The live storefront reads this row DIRECTLY (0019 Q1 live-on-save),
  // so a keepalive PUT on tab-hide/pagehide would poison the LIVE presetId with the unpublished theme
  // while its documents still sit on the old theme's `default` handle — the "neither theme" hybrid the
  // seller sees in both the deployed store and the builder Customize view. A staged theme only ever
  // goes live through publishTheme(); never flush it here.
  if (store().getState().themeStagedForPublish) return false;
  if (!pendingSave || inFlight) return false;

  const theme = store().getState().themeSettings;
  const token = getToken();
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  try {
    void fetch(`${deriveApiBaseUrl()}${THEME_PATH}`, {
      method: 'PUT',
      keepalive: true,
      credentials: 'include',
      headers,
      // This PUT bypasses `builderApi.saveTheme`, so it has to state the local-media rule itself.
      body: JSON.stringify(stripLocalThemeMedia(theme)),
    });
    pendingSave = false;
    return true;
  } catch {
    return false;
  }
}

// ── force-flush trigger wiring (window events) — installed once by the shell ───

let triggersInstalled = false;

/**
 * Install the theme force-flush window listeners. Idempotent (StrictMode double-mount → no-op). Returns a
 * teardown. Separate from `installAutosaveTriggers` so the two engines stay decoupled.
 *  - `visibilitychange → hidden` + `pagehide` → keepalive last-save (carries the Bearer).
 *  - `online` → resume immediately (cancel the backoff wait) if a theme edit is stuck offline.
 */
export function installThemeSaveTriggers(): () => void {
  if (triggersInstalled) return () => undefined;
  triggersInstalled = true;

  const onVisibility = (): void => {
    if (document.visibilityState === 'hidden') keepaliveThemeFlush();
  };
  const onPageHide = (): void => {
    keepaliveThemeFlush();
  };
  const onOnline = (): void => {
    clearBackoff();
    backoffMs = BACKOFF_MIN_MS;
    if (pendingSave && store().getState().themeSaveState === 'offline') {
      void flushThemeSave();
    }
  };

  document.addEventListener('visibilitychange', onVisibility);
  window.addEventListener('pagehide', onPageHide);
  window.addEventListener('online', onOnline);

  return () => {
    document.removeEventListener('visibilitychange', onVisibility);
    window.removeEventListener('pagehide', onPageHide);
    window.removeEventListener('online', onOnline);
    triggersInstalled = false;
  };
}
